import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Badge, ProgressBar, Container, Row, Col, Form } from 'react-bootstrap';
import { CheckCircle, XCircle, AlertTriangle, Wind, Trophy, ArrowUp, Award, Star } from 'lucide-react';
import { motion, useAnimation, AnimatePresence, useAnimate, MotionConfig } from 'framer-motion';
import { useParams } from 'react-router-dom';
import './Stackgame.css';

// Game configuration constants
const MAX_TOWER_HEIGHT = 15;
const BASE_BLOCK_WIDTH = 80;
const WIND_CHANCE = 0.3;
const WOBBLE_FACTOR = 10;
const COLORS = [
  'bg-primary',
  'bg-success',
  'bg-info',
  'bg-warning',
  'bg-danger',
];

export default function StackAttackGame() {
  // Get URL parameters
  const { room, topic_id } = useParams();
  
  // Game state
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [blocks, setBlocks] = useState([]);
  const [windIntensity, setWindIntensity] = useState(0);
  const [windDirection, setWindDirection] = useState('left');
  const [timeLeft, setTimeLeft] = useState(30);
  const [highScore, setHighScore] = useState(0);
  const [isHighScore, setIsHighScore] = useState(false);
  
  // Question state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const windAnimation = useAnimation();
  const towerRef = useRef(null);
  
  // Timer
  const [timerActive, setTimerActive] = useState(false);
  const [timerInterval, setTimerInterval] = useState(null);
  
  // Wind effect interval
  const [windInterval, setWindInterval] = useState(null);

  // Sample questions for different levels
  const sampleQuestions = [
    // Level 1 - Easy
    {
      id: 1,
      question: "Which element has the symbol 'Na'?",
      correct_answer: "Sodium",
      options: ["Nitrogen", "Sodium", "Neon", "Nickel"],
      category: "chemistry",
      difficulty: "easy",
      level: 1
    },
    {
      id: 2,
      question: "What is the value of π (pi) to two decimal places?",
      correct_answer: "3.14",
      options: ["3.14", "3.15", "3.16", "3.17"],
      category: "math",
      difficulty: "easy",
      level: 1
    },
    // Level 2 - Medium
    {
      id: 3,
      question: "Which of these is Newton's First Law?",
      correct_answer: "An object in motion stays in motion unless acted upon by an external force",
      options: [
        "Force equals mass times acceleration",
        "An object in motion stays in motion unless acted upon by an external force",
        "For every action, there is an equal and opposite reaction",
        "Energy cannot be created or destroyed"
      ],
      category: "physics",
      difficulty: "medium",
      level: 2
    },
    {
      id: 4,
      question: "In computer science, what does CPU stand for?",
      correct_answer: "Central Processing Unit",
      options: ["Central Processing Unit", "Computer Personal Unit", "Central Processor Underneath", "Core Processing Unit"],
      category: "computer science",
      difficulty: "medium",
      level: 2
    },
    // Level 3 - Hard
    {
      id: 5,
      question: "What is the period of a pendulum with length 1 meter on Earth?",
      correct_answer: "About 2 seconds",
      options: ["About 1 second", "About 2 seconds", "About 3 seconds", "About 4 seconds"],
      category: "physics",
      difficulty: "hard",
      level: 3
    },
    {
      id: 6,
      question: "Which algorithm has the worst time complexity?",
      correct_answer: "Bubble Sort - O(n²)",
      options: ["Binary Search - O(log n)", "Merge Sort - O(n log n)", "Bubble Sort - O(n²)", "Quick Sort - O(n log n)"],
      category: "computer science",
      difficulty: "hard",
      level: 3
    },
    // Level 4 - Very Hard
    {
      id: 7,
      question: "What is the Schrödinger equation used for in quantum mechanics?",
      correct_answer: "To determine how quantum states evolve over time",
      options: [
        "To determine the position of electrons",
        "To calculate the mass of atoms",
        "To determine how quantum states evolve over time",
        "To measure nuclear radiation"
      ],
      category: "physics",
      difficulty: "very hard",
      level: 4
    },
    {
      id: 8,
      question: "In calculus, what is the derivative of e^x?",
      correct_answer: "e^x",
      options: ["e^(x-1)", "xe^x", "e^x", "ln(x)"],
      category: "math",
      difficulty: "very hard",
      level: 4
    },
    // Level 5 - Expert
    {
      id: 9,
      question: "Which data structure is most efficient for implementing a priority queue?",
      correct_answer: "Heap",
      options: ["Array", "Linked List", "Heap", "Binary Search Tree"],
      category: "computer science",
      difficulty: "expert",
      level: 5
    },
    {
      id: 10,
      question: "What is the chemical formula for glucose?",
      correct_answer: "C₆H₁₂O₆",
      options: ["C₆H₁₂O₆", "C₂H₅OH", "H₂O", "CO₂"],
      category: "chemistry",
      difficulty: "expert",
      level: 5
    }
  ];

  // Effect to set player name from URL if available
  useEffect(() => {
    if (room) {
      setPlayerName(`Player-${room}`);
    }
    
    // If we have topic_id, we could use it to filter questions
    // For now we'll just log it
    if (topic_id) {
      console.log(`Topic ID: ${topic_id}`);
      // Future enhancement: fetch topic-specific questions
    }
  }, [room, topic_id]);

  // Initialize or reset the game
  const startGame = () => {
    if (!playerName.trim()) {
      alert("Please enter your name to start the game!");
      return;
    }
    
    // Reset game state
    setLevel(1);
    setScore(0);
    setBlocks([]);
    setIsGameStarted(true);
    setIsGameOver(false);
    setTimeLeft(30);
    setTimerActive(true);
    setIsHighScore(false);
    setWindIntensity(0);
    
    // Start with base block
    addBaseBlock();
    
    // Get first question
    getQuestionForLevel(1);
    
    // Start timer
    startTimer();
    
    // Start wind simulation
    startWindSimulation();
  };

  // Add base block to the tower (foundation)
  const addBaseBlock = () => {
    const baseBlock = {
      id: 0,
      correct: true,
      width: 100, // Full width
      position: 0,
      color: 'bg-dark',
      content: 'Foundation',
      wobble: false
    };
    
    setBlocks([baseBlock]);
  };

  // Get a question appropriate for the current level
  const getQuestionForLevel = (currentLevel) => {
    // Filter questions that are suitable for the current level
    const questionsForLevel = sampleQuestions.filter(q => 
      q.level <= currentLevel
    );

    if (questionsForLevel.length === 0) {
      // If no questions found for level, use highest available level
      const maxAvailableLevel = Math.max(...sampleQuestions.map(q => q.level));
      const fallbackQuestions = sampleQuestions.filter(q => 
        q.level === maxAvailableLevel
      );
      const randomIndex = Math.floor(Math.random() * fallbackQuestions.length);
      setCurrentQuestion(fallbackQuestions[randomIndex]);
    } else {
      const randomIndex = Math.floor(Math.random() * questionsForLevel.length);
      setCurrentQuestion(questionsForLevel[randomIndex]);
    }
  };

  // Start the game timer
  const startTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    const interval = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(interval);
          endGame();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    
    setTimerInterval(interval);
  };

  // Start wind simulation
  const startWindSimulation = () => {
    if (windInterval) {
      clearInterval(windInterval);
    }
    
    const interval = setInterval(() => {
      // 30% chance of wind changing
      if (Math.random() < WIND_CHANCE) {
        const newIntensity = Math.floor(Math.random() * 5); // 0-4 intensity
        const newDirection = Math.random() < 0.5 ? 'left' : 'right';
        
        setWindIntensity(newIntensity);
        setWindDirection(newDirection);
        
        // Animate wind indicator
        windAnimation.start({
          x: newDirection === 'left' ? -10 : 10,
          transition: { type: 'spring', stiffness: 300, damping: 10 }
        });
        
        // Apply wobble effect to blocks
        if (newIntensity > 0) {
          applyWobbleToBlocks();
        }
      }
    }, 5000); // Check for wind changes every 5 seconds
    
    setWindInterval(interval);
  };

  // Apply wobble effect to some blocks in the tower
  const applyWobbleToBlocks = () => {
    setBlocks(prevBlocks => {
      const newBlocks = [...prevBlocks];
      
      // Don't wobble the base block
      for (let i = 1; i < newBlocks.length; i++) {
        // Higher blocks have more chance to wobble - especially the top blocks
        const heightFactor = (i / newBlocks.length) ** 2; // Square it to emphasize top blocks
        const wobbleChance = heightFactor * windIntensity * 0.3;
        
        // Incorrect blocks are more likely to wobble
        const correctnessFactor = newBlocks[i].correct ? 1 : 1.5;
        
        // Calculate final wobble chance
        const finalWobbleChance = wobbleChance * correctnessFactor;
        
        if (Math.random() < finalWobbleChance) {
          // Calculate wobble amount based on height, wind intensity and correctness
          const wobbleAmount = 
            Math.random() * WOBBLE_FACTOR * 
            (windIntensity / 3) * 
            (newBlocks[i].correct ? 1 : 1.5) * 
            (i / newBlocks.length + 0.5);
          
          newBlocks[i] = { 
            ...newBlocks[i], 
            wobble: true,
            position: newBlocks[i].position + 
              ((windDirection === 'left' ? -1 : 1) * wobbleAmount)
          };
        } else {
          // Gradually reduce position back to original if no longer wobbling
          if (newBlocks[i].wobble) {
            const currentPos = newBlocks[i].position;
            // Move 20% closer to where it should be
            const newPos = currentPos * 0.8 + 
              (newBlocks[i].correct ? 0 : (Math.random() < 0.5 ? -10 : 10)) * 0.2;
            
            newBlocks[i] = { 
              ...newBlocks[i], 
              wobble: false,
              position: newPos
            };
          } else {
            newBlocks[i] = { ...newBlocks[i], wobble: false };
          }
        }
      }
      
      return newBlocks;
    });
  };

  // Handle answer submission
  const handleAnswerSubmit = (answer) => {
    if (!currentQuestion || showFeedback) return;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    // Check if answer is correct
    const correct = answer === currentQuestion.correct_answer;
    setIsCorrect(correct);
    
    if (correct) {
      // Calculate score based on level and time left
      const pointsEarned = level * 10 + timeLeft;
      setScore(prevScore => prevScore + pointsEarned);
      
      // Add block to tower
      addBlockToTower(true);
      
      // Check if level should increase (every 3 blocks)
      if (blocks.length % 3 === 0) {
        setLevel(prevLevel => Math.min(prevLevel + 1, 5));
      }
      
      // Add time bonus for correct answer
      setTimeLeft(prevTime => Math.min(prevTime + 5, 30));
      
    } else {
      // Add a misaligned block for wrong answer
      addBlockToTower(false);
      
      // Reduce time for wrong answer
      setTimeLeft(prevTime => Math.max(prevTime - 3, 0));
    }
    
    // Delay to show feedback before moving to next question
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedAnswer('');
      
      // Game is over if tower is complete or tipped over
      if (blocks.length >= MAX_TOWER_HEIGHT) {
        endGame();
      } else {
        // Get next question
        getQuestionForLevel(level);
      }
    }, 1500);
  };

  // Add a block to the tower
  const addBlockToTower = (correct) => {
    setBlocks(prevBlocks => {
      const newBlockId = prevBlocks.length;
      
      // Calculate width and position based on correctness
      // Correct blocks maintain the width of the previous block
      // Wrong blocks are slightly narrower
      let width = correct ? 
        prevBlocks[prevBlocks.length - 1].width : 
        prevBlocks[prevBlocks.length - 1].width * 0.85;
      
      // Make sure width doesn't get too small
      width = Math.max(width, 40);
      
      // Calculate position (slight misalignment for wrong answers)
      // This creates the effect of blocks not being placed properly
      let position = correct ? 0 : (Math.random() < 0.5 ? -15 : 15);
      
      // Apply wind effect to position - stronger wind has more impact
      if (windIntensity > 0) {
        position += (windDirection === 'left' ? -1 : 1) * (windIntensity * 3);
      }
      
      // Random color from the COLORS array - pick colors strategically based on correct/wrong
      const colorIndex = correct 
        ? Math.floor(Math.random() * 3) // Use first 3 colors for correct answers (blue, green, info)
        : 3 + Math.floor(Math.random() * 2); // Use last 2 colors for wrong answers (warning, danger)
      
      // Create the new block
      const newBlock = {
        id: newBlockId,
        correct,
        width,
        position,
        color: COLORS[colorIndex],
        content: `Block ${newBlockId}`,
        // Wrong answers or blocks affected by strong wind are more likely to wobble
        wobble: !correct || (windIntensity > 3 && Math.random() < 0.7)
      };
      
      return [...prevBlocks, newBlock];
    });
  };

  // State for falling blocks animation
  const [isTowerFalling, setIsTowerFalling] = useState(false);
  const [fallingBlocks, setFallingBlocks] = useState([]);
  
  // End the game with tower collapse animation
  const endGame = () => {
    // Start tower falling animation
    setIsTowerFalling(true);
    
    // Create a copy of blocks for the falling animation
    setFallingBlocks([...blocks]);
    
    // Set timer to complete the animation before showing game over screen
    setTimeout(() => {
      setTimerActive(false);
      setIsGameOver(true);
      
      // Clear intervals
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      
      if (windInterval) {
        clearInterval(windInterval);
        setWindInterval(null);
      }
      
      // Check if high score
      if (score > highScore) {
        setHighScore(score);
        setIsHighScore(true);
      }
      
      // Scroll to see the full tower
      if (towerRef.current) {
        towerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 2500); // Wait for falling animation to complete
  };

  // Restart the game
  const restartGame = () => {
    setIsGameStarted(false);
    setIsGameOver(false);
  };

  // Timer effect
  useEffect(() => {
    if (timerActive && !timerInterval) {
      startTimer();
    }
    
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerActive, timerInterval]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
      if (windInterval) {
        clearInterval(windInterval);
      }
    };
  }, [timerInterval, windInterval]);

  // Render welcome screen
  if (!isGameStarted) {
    return (
      <Container className="py-5">
        <Card className="welcome-card">
          <div className="welcome-header">
            <h1 className="game-title display-4 mb-3">STEM Stack Tower Challenge</h1>
            <p className="text-white mb-0">
              Test your STEM knowledge while building the tallest tower you can!
            </p>
          </div>
          <Card.Body className="p-4">
            <Row>
              <Col md={7}>
                <div className="mb-4">
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Enter Your Name:</Form.Label>
                    <Form.Control
                      type="text"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Your name"
                      className="py-2"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Select Difficulty:</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5].map((l) => (
                        <Button
                          key={l}
                          variant={level === l ? "primary" : "outline-secondary"}
                          onClick={() => setLevel(l)}
                          className="px-3 py-2"
                        >
                          {l === 1 && "Easy"}
                          {l === 2 && "Medium"}
                          {l === 3 && "Hard"}
                          {l === 4 && "Expert"}
                          {l === 5 && "Master"}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Select Wind Level:</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4].map((w) => (
                        <Button
                          key={w}
                          variant={windIntensity === w ? "primary" : "outline-secondary"}
                          onClick={() => setWindIntensity(w)}
                          className="px-3 py-2"
                        >
                          {w === 0 && "None"}
                          {w === 1 && "Light"}
                          {w === 2 && "Medium"}
                          {w === 3 && "Strong"}
                          {w === 4 && "Storm"}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                  
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      size="lg" 
                      onClick={startGame} 
                      disabled={!playerName.trim()}
                      className="py-3 mt-3"
                    >
                      <i className="bi bi-play-fill me-2"></i>
                      Start Game
                    </Button>
                  </div>
                </div>
              </Col>
              
              <Col md={5}>
                <div className="bg-light p-4 rounded-3 h-100">
                  <h4 className="text-primary mb-3">How To Play:</h4>
                  <ul className="mb-4">
                    <li className="mb-2">Answer STEM questions correctly to add stable blocks to your tower</li>
                    <li className="mb-2">Incorrect answers create unstable blocks that may wobble</li>
                    <li className="mb-2">Wind intensity increases as your tower grows taller</li>
                    <li className="mb-2">Build as tall as you can before time runs out or your tower falls</li>
                    <li className="mb-2">Higher difficulty = harder questions but more points!</li>
                  </ul>
                  
                  <div className="alert alert-warning d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <div>
                      Watch out for the wind! It can topple your tower if it gets too unstable.
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Game over screen
  if (isGameOver) {
    const correctCount = blocks.filter(b => b.correct && b.id > 0).length;
    const wrongCount = blocks.filter(b => !b.correct).length;
    const accuracyPercentage = blocks.length > 1 
      ? Math.round((correctCount / (correctCount + wrongCount)) * 100) 
      : 0;

    return (
      <div className="container-fluid mx-auto mt-4">
        <Card className="shadow">
          <Card.Header className="bg-primary text-white text-center p-4 position-relative">
            <div className="position-absolute top-0 start-50 translate-middle">
              <div className="bg-warning p-4 rounded-circle">
                <Trophy className="text-white" size={32} />
              </div>
            </div>
            
            <div className="mt-5 pt-2">
              <Card.Title className="fs-1 mb-2">Game Over!</Card.Title>
              <Card.Text className="fs-5">
                Your tower reached {blocks.length - 1} blocks!
              </Card.Text>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h2 className="fs-4 fw-bold mb-2">{playerName}'s Score</h2>
              <p className="fs-1 fw-bold text-primary">{score} points</p>
              
              {isHighScore && (
                <div className="d-flex align-items-center justify-content-center mt-2">
                  <Award className="text-warning me-2" size={24} />
                  <span className="fs-5 fw-semibold text-warning">New High Score!</span>
                </div>
              )}
            </div>
            
            <div className="row g-4">
              <div className="col-md-5">
                <h3 className="fs-5 fw-semibold mb-3">Performance</h3>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Accuracy</span>
                    <span className="fw-medium">{accuracyPercentage}%</span>
                  </div>
                  <ProgressBar variant="success" now={accuracyPercentage} />
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Tower Height</span>
                    <span className="fw-medium">{blocks.length - 1}/{MAX_TOWER_HEIGHT}</span>
                  </div>
                  <ProgressBar variant="primary" now={((blocks.length - 1) / MAX_TOWER_HEIGHT) * 100} />
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Level Reached</span>
                    <span className="fw-medium">{level}/5</span>
                  </div>
                  <ProgressBar variant="info" now={(level / 5) * 100} />
                </div>
                
                <div className="bg-light p-3 rounded mt-4">
                  <div className="row">
                    <div className="col-6 d-flex align-items-center">
                      <div className="bg-success rounded-circle me-2" style={{ width: "10px", height: "10px" }}></div>
                      <span>Correct blocks: {correctCount}</span>
                    </div>
                    <div className="col-6 d-flex align-items-center">
                      <div className="bg-danger rounded-circle me-2" style={{ width: "10px", height: "10px" }}></div>
                      <span>Wrong blocks: {wrongCount}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Show the tower */}
              <div className="col-md-7">
                <h3 className="fs-5 fw-semibold mb-3">Your Tower</h3>
                <div className="tower-container game-over-tower">
                  {/* Ground/floor effect */}
                  <div className="tower-floor"></div>
                  
                  {/* Tower container using grid layout */}
                  <div className="tower-blocks">
                    {blocks.map((block, index) => (
                      <div 
                        key={block.id}
                        className={`tower-block ${index === 0 ? "bg-dark" : block.color}`}
                        style={{ 
                          width: `${block.width}%`,
                          transform: `translateX(${block.position}px)`,
                          gridRow: blocks.length - index,
                        }}
                      >
                        {index === 0 ? "Foundation" : `Block ${index}`}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-2">
              <Button 
                onClick={restartGame} 
                variant="primary"
                size="lg" 
                className="w-100"
              >
                Play Again
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Main game screen
  return (
    <div className="container-fluid mx-auto py-4">
      <div className="row g-4">
        {/* Left panel - Tower and stats */}
        <div className="col-lg-6">
          <Card className="game-info-panel">
            <Card.Header className="bg-dark text-white p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Award className="me-2" size={20} />
                  <span className="fs-5 fw-semibold">{playerName}</span>
                </div>
                <Badge bg="secondary" pill className="px-3 py-2">Level {level}</Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="fs-6 fw-semibold d-flex align-items-center">
                    <Trophy className="me-2 text-warning" size={18} />
                    <span>Score:</span>
                  </div>
                  <div className="fs-4 fw-bold text-primary">{score}</div>
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between fs-6">
                    <span className="d-flex align-items-center">
                      <i className="bi bi-clock me-2"></i>
                      Time Left:
                    </span>
                    <span className={`fw-bold ${timeLeft < 10 ? 'text-danger' : ''}`}>{timeLeft}s</span>
                  </div>
                  <ProgressBar 
                    now={(timeLeft/30)*100} 
                    variant={timeLeft < 10 ? 'danger' : timeLeft < 20 ? 'warning' : 'success'}
                    className="shadow-sm"
                  />
                </div>
                
                <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded-3 mb-3">
                  <div className="fs-6 fw-semibold d-flex align-items-center">
                    <Wind className="me-2 text-info" size={18} />
                    <span>Wind:</span>
                  </div>
                  <div className="d-flex align-items-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Wind 
                        key={i} 
                        className={i < windIntensity ? "text-primary" : "text-secondary opacity-50"} 
                        size={16}
                        strokeWidth={i < windIntensity ? 2.5 : 1.5}
                      />
                    ))}
                    <motion.div
                      animate={windAnimation}
                      className={`ms-2 small fw-bold ${windDirection === 'left' ? 'text-primary' : 'text-info'}`}
                    >
                      {windDirection === 'left' ? '←' : '→'}
                    </motion.div>
                  </div>
                </div>
                
                <div className="p-2 bg-light rounded-3">
                  <div className="d-flex justify-content-between">
                    <span className="fs-6 fw-semibold d-flex align-items-center">
                      <ArrowUp className="me-2 text-success" size={18} />
                      Height:
                    </span>
                    <span className="fs-6 fw-bold">{blocks.length - 1}/{MAX_TOWER_HEIGHT} blocks</span>
                  </div>
                  <ProgressBar 
                    now={((blocks.length - 1) / MAX_TOWER_HEIGHT) * 100} 
                    variant="success" 
                    className="mt-2 shadow-sm" 
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
          
          {/* Tower display */}
          <div className="tower-container" ref={towerRef}>
            {/* Ground/floor effect */}
            <div className="tower-floor"></div>
            
            {/* Tower falling effect shown when game ends */}
            <AnimatePresence>
              {isTowerFalling && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="falling-tower-container"
                >
                  <div className="position-relative h-100 w-100 overflow-hidden">
                    {fallingBlocks.map((block, index) => (
                      index > 0 && (
                        <motion.div
                          key={`falling-${block.id}`}
                          className={`falling-block ${block.color}`}
                          style={{ 
                            width: `${block.width * 0.8}%`,
                            left: `${50 - (block.width * 0.4)}%`,
                            top: `${20 + (index * 3)}%`,
                          }}
                          initial={{ 
                            y: 0, 
                            x: block.position, 
                            rotate: block.wobble ? 5 : 0,
                            opacity: 1,
                            scale: 1
                          }}
                          animate={{ 
                            y: [0, 50 * (index / 2), 500 + Math.random() * 100],
                            x: [
                              block.position, 
                              block.position + (Math.random() * 60 - 30),
                              block.position + (Math.random() * 150 - 75)
                            ],
                            rotate: [
                              0, 
                              (block.id % 2 === 0 ? 1 : -1) * Math.random() * 180, 
                              (block.id % 2 === 0 ? 1 : -1) * Math.random() * 360
                            ],
                            opacity: [1, 1, 0],
                            scale: [1, 1, 0.8],
                            filter: ["drop-shadow(0 5px 15px rgba(0,0,0,0.3))", "drop-shadow(0 15px 25px rgba(0,0,0,0.2))"]
                          }}
                          transition={{ 
                            duration: 2 + Math.random() * 0.5,
                            ease: "easeIn",
                            times: [0, 0.4, 1],
                            delay: 0.05 * (fallingBlocks.length - index) // Start from top blocks
                          }}
                        >
                          {`Block ${index}`}
                          {/* Add dust particles */}
                          <div className="dust-particle"></div>
                          <div className="dust-particle"></div>
                          <div className="impact-effect"></div>
                          <motion.span 
                            className="position-absolute" 
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              top: '30%',
                              left: '20%'
                            }}
                            animate={{
                              opacity: [0, 0.8, 0],
                              y: [0, -20, -40],
                              x: [0, 10, 20]
                            }}
                            transition={{
                              duration: 1,
                              delay: 0.2 + Math.random() * 0.3
                            }}
                          />
                          <motion.span 
                            className="position-absolute" 
                            style={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              bottom: '40%',
                              right: '25%'
                            }}
                            animate={{
                              opacity: [0, 0.8, 0],
                              y: [0, -15, -30],
                              x: [0, -8, -16]
                            }}
                            transition={{
                              duration: 0.8,
                              delay: 0.1 + Math.random() * 0.3
                            }}
                          />
                          <motion.span 
                            className="position-absolute" 
                            style={{
                              width: '5px',
                              height: '5px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              top: '10%',
                              right: '20%'
                            }}
                            animate={{
                              opacity: [0, 0.7, 0],
                              y: [0, -25, -45],
                              x: [0, -12, -24]
                            }}
                            transition={{
                              duration: 1.2,
                              delay: 0.3 + Math.random() * 0.3
                            }}
                          />
                        </motion.div>
                      )
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Tower container using grid layout */}
            <div className="tower-blocks">
              {blocks.map((block, index) => (
                <motion.div
                  key={block.id}
                  className={`tower-block ${index === 0 ? "bg-dark" : block.color} ${index === 0 ? "border-bottom border-dark" : ""}`}
                  style={{ 
                    width: `${block.width}%`,
                    transform: `translateX(${block.position}px)`,
                    gridRow: blocks.length - index, // Position from bottom to top
                  }}
                  // Animate blocks coming in from below (ground up)
                  initial={{ opacity: 0, y: 50, scale: 0.8 }}
                  animate={{
                    opacity: 1, 
                    y: 0,
                    scale: 1,
                    rotate: block.wobble ? [0, 2, -2, 0] : 0, // More pronounced wobble
                  }}
                  transition={{
                    opacity: { duration: 0.4 },
                    y: { 
                      duration: 0.8, 
                      type: "spring", 
                      stiffness: 120, 
                      damping: 8,
                      delay: 0.1 * (blocks.length - index - 1) // Stagger delay based on block position
                    },
                    scale: { duration: 0.5 },
                    rotate: { 
                      repeat: block.wobble ? Infinity : 0, 
                      duration: block.wobble ? 0.3 : 0.5,
                      repeatType: "mirror"
                    }
                  }}
                >
                  {index === 0 ? (
                    <span className="d-flex align-items-center justify-content-center">
                      <motion.span 
                        animate={{ scale: [1, 1.1, 1] }} 
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="fw-bold"
                      >
                        Foundation
                      </motion.span>
                    </span>
                  ) : (
                    <span className="d-flex align-items-center justify-content-center">
                      {`Block ${index}`}
                      {block.correct && (
                        <span className="correct-star">★</span>
                      )}
                    </span>
                  )}
                  
                  {/* Add glow effect for correct blocks */}
                  {block.correct && index > 0 && (
                    <div className="block-glow" />
                  )}
                  
                  {/* Add shadow beneath each block */}
                  <motion.div 
                    className="tower-block-shadow"
                    animate={{ width: block.wobble ? ["100%", "90%", "100%"] : "100%" }}
                    transition={{ repeat: block.wobble ? Infinity : 0, duration: 0.5 }}
                  ></motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right panel - Questions */}
        <div className="col-lg-6">
          <Card className="question-panel h-100">
            <Card.Header className="bg-primary text-white p-3">
              <div className="d-flex justify-content-between align-items-center">
                <Card.Title className="fs-4 mb-0">STEM Question Challenge</Card.Title>
                <Badge 
                  bg={
                    level === 1 ? 'success' : 
                    level === 2 ? 'primary' : 
                    level === 3 ? 'info' : 
                    level === 4 ? 'warning' : 'danger'
                  }
                  pill
                  className="px-3 py-2"
                >
                  {level === 1 ? 'Easy' : 
                   level === 2 ? 'Medium' : 
                   level === 3 ? 'Hard' : 
                   level === 4 ? 'Very Hard' : 'Expert'}
                </Badge>
              </div>
              <Card.Text className="text-white-50 mb-0 mt-1">
                Answer correctly to add a block to your tower!
              </Card.Text>
            </Card.Header>
            <Card.Body className="p-4">
              {currentQuestion && (
                <div className="mb-4">
                  <div className="alert alert-warning d-flex align-items-center mb-4">
                    <AlertTriangle className="flex-shrink-0 me-2" size={18} />
                    <span>Wrong answers will create unstable blocks! Watch out for wind!</span>
                  </div>
                  
                  <h2 className="fs-4 fw-semibold mb-4">{currentQuestion.question}</h2>
                  <div className="d-grid gap-3">
                    {currentQuestion.options.map((option) => (
                      <Button
                        key={option}
                        onClick={() => handleAnswerSubmit(option)}
                        variant={
                          showFeedback
                            ? option === currentQuestion.correct_answer
                              ? "success" 
                              : option === selectedAnswer && option !== currentQuestion.correct_answer
                              ? "danger" 
                              : "outline-primary"
                            : "outline-primary"
                        }
                        className="option-button text-start p-3"
                        disabled={showFeedback}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{option}</span>
                          {showFeedback && option === currentQuestion.correct_answer && (
                            <CheckCircle size={20} />
                          )}
                          {showFeedback && option === selectedAnswer && option !== currentQuestion.correct_answer && (
                            <XCircle size={20} />
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Feedback message */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`alert ${isCorrect ? "alert-success" : "alert-danger"} d-flex align-items-center p-3`}
                  >
                    {isCorrect ? (
                      <CheckCircle className="me-2 flex-shrink-0" size={20} />
                    ) : (
                      <XCircle className="me-2 flex-shrink-0" size={20} />
                    )}
                    <div>
                      <p className="fw-medium mb-0">
                        {isCorrect 
                          ? `Correct! Adding a stable block to your tower.` 
                          : `Incorrect! Adding an unstable block to your tower.`}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Add a floating indicator for current tower height */}
      <motion.div 
        className="position-fixed bottom-0 end-0 m-4 p-3 bg-dark text-white rounded-circle d-flex align-items-center justify-content-center"
        style={{ width: '60px', height: '60px', zIndex: 1000, boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <div className="text-center">
          <div className="fs-5 fw-bold">{blocks.length - 1}</div>
          <div className="small">blocks</div>
        </div>
      </motion.div>
    </div>
  );
}
